import { AuthTokensSchema, type AuthTokens } from "@steelbill/types";
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  saveSession,
} from "@/lib/token-storage";
import { ApiError, NETWORK_FAILURE_STATUS, type ApiErrorDetail } from "@/lib/api-error";
import { API_BASE_URL } from "@/lib/env";

/**
 * The typed fetch wrapper. Every request the SPA makes goes through here.
 *
 * Responsibilities, and nothing else:
 *   1. Join the path onto `API_BASE_URL` (from config — §13, see `env.ts`).
 *   2. Attach the bearer token (§1.1 — bearer, not cookie-only, so the
 *      Capacitor shell works unchanged).
 *   3. On 401, refresh ONCE, retry ONCE, then sign out and go to login.
 *   4. Turn `{ error: { message, details? } }` into a typed {@link ApiError}
 *      that still carries the field-level detail, so forms can show it inline.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *   • It does not compute or verify totals. The server is the only authority
 *     (§6) and `@steelbill/tax` is the only arithmetic (§3.1).
 *   • It does not format money. Money crosses the wire as integer paise and is
 *     formatted by `formatPaise` at the point of display (CLAUDE.md).
 *   • It does not cache or retry beyond the single auth retry. Caching is
 *     TanStack Query's job (`query-client.ts`); offline drafts are Dexie's
 *     (Phase 5).
 */

/* -------------------------------------------------------------------------- */
/* Session-expiry handoff                                                      */
/* -------------------------------------------------------------------------- */

type SessionExpiredHandler = () => void;

let onSessionExpired: SessionExpiredHandler | null = null;

/**
 * Registered by `AuthProvider` so an expired session navigates through React
 * Router (preserving the attempted URL) rather than reloading the page.
 *
 * An inverted dependency, on purpose: `api.ts` must not import the auth context
 * — the auth context imports `api.ts` to log in, and a cycle between the two is
 * how a bundler ends up handing one of them `undefined` at module-init time.
 * The fallback below means this module still behaves correctly if nobody
 * registers, which is the case in unit tests.
 */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  onSessionExpired = handler;
}

function expireSession(): void {
  clearSession();
  if (onSessionExpired !== null) {
    onSessionExpired();
    return;
  }
  // No handler (tests, or a failure before the provider mounted). A hard
  // navigation is the honest fallback — never leave the app rendering
  // authenticated chrome around data it can no longer load.
  if (typeof window !== "undefined") window.location.assign("/login");
}

/* -------------------------------------------------------------------------- */
/* Request options                                                             */
/* -------------------------------------------------------------------------- */

export interface ApiRequestOptions {
  /** Query string values. `undefined` and `null` entries are omitted. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** JSON body. Serialised here; do not pre-stringify. */
  body?: unknown;
  /** For TanStack Query cancellation and for aborting a search-as-you-type. */
  signal?: AbortSignal;
  /**
   * Skip the bearer token and the refresh dance. Only `/auth/*` uses this — a
   * login request that 401s means "wrong PIN", and retrying it after a token
   * refresh would be nonsense.
   */
  anonymous?: boolean;
  /**
   * This request PROVES A CREDENTIAL, so a 401 from it means "that is not your
   * current PIN or password" — not "your session expired".
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * WITHOUT THIS FLAG, ONE MISTYPED DIGIT SIGNS THE PERSON OUT OF THE APP.
   *
   * `POST /profile/credential` and `POST /profile/identifier` re-authenticate
   * server-side (`verifyCurrentCredential`) and answer 401 on a wrong current
   * secret — the same status an expired token produces. The refresh-and-retry
   * below cannot tell them apart: it would refresh successfully (the session is
   * perfectly good), replay the request, receive the same 401 because the PIN is
   * still wrong, and then `expireSession()` — throwing the owner out to the login
   * screen for a typo, with "Please sign in again." where the reason should be.
   *
   * AND THE REPLAY IS NOT FREE. Each attempt costs a bcrypt cost-12 comparison
   * (~250ms, single-threaded) and counts against the PER-ACCOUNT LOCKOUT, so a
   * silent retry spends the budget twice per typo and freezes the account in half
   * the attempts the server intended.
   *
   * THE TOKEN IS STILL SENT. This is not `anonymous`: the route needs the bearer
   * to know whose account to prove against. Only the retry is given up.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  reauthenticating?: boolean;
  headers?: Record<string, string>;
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function buildUrl(path: string, query: ApiRequestOptions["query"]): string {
  const normalised = path.startsWith("/") ? path : `/${path}`;
  const url = `${API_BASE_URL}${normalised}`;
  if (query === undefined) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search === "" ? url : `${url}?${search}`;
}

/* -------------------------------------------------------------------------- */
/* Response → value, or a typed error                                          */
/* -------------------------------------------------------------------------- */

/** Reads `{ error: { message, details? } }` without trusting any of it. */
function parseErrorEnvelope(payload: unknown): {
  message: string | null;
  details: ApiErrorDetail[];
} {
  const error = (payload as { error?: unknown } | null | undefined)?.error;
  if (typeof error !== "object" || error === null) return { message: null, details: [] };

  const message = (error as { message?: unknown }).message;
  const rawDetails = (error as { details?: unknown }).details;

  const details: ApiErrorDetail[] = Array.isArray(rawDetails)
    ? rawDetails.flatMap((entry) => {
        if (typeof entry !== "object" || entry === null) return [];
        const field = (entry as ApiErrorDetail).field;
        const detailMessage = (entry as ApiErrorDetail).message;
        if (typeof field !== "string" || typeof detailMessage !== "string") return [];
        return [{ field, message: detailMessage }];
      })
    : [];

  return { message: typeof message === "string" ? message : null, details };
}

/**
 * The message the owner sees when the server did not supply a usable one.
 *
 * NOT a raw status code and never a stack. §11.1: "errors in plain language".
 * These strings are the last resort — every screen is expected to catch the
 * `ApiError` and render a translated message through `t()` (§11.4). They exist
 * so that a path nobody anticipated still produces a sentence rather than
 * "[object Object]".
 */
/**
 * The exact string `describeError` (apps/api/src/app.ts) uses when it has
 * flattened something that was NOT a deliberate `HttpError` — a thrown `Error`,
 * a Prisma failure, a bug. It is a status line, not a sentence written for a
 * person, and it is the one 5xx message this client refuses to show.
 *
 * PINNED HERE AS A CONSTANT AND ASSERTED BY A TEST rather than matched loosely,
 * because the whole distinction the code below draws rests on it: match too
 * broadly and a real diagnosis is swallowed again; match nothing and a crash's
 * status line is presented as advice. `describeError`'s literal is the contract.
 */
const SERVER_CRASH_MESSAGE = "Internal Server Error";

function fallbackMessage(status: number): string {
  if (status === NETWORK_FAILURE_STATUS) return "No internet connection.";
  if (status === 401) return "Please sign in again.";
  if (status === 403) return "You do not have permission to do this.";
  if (status === 404) return "Not found.";
  if (status === 429) return "Too many attempts. Please wait a moment.";
  if (status >= 500) return "Something went wrong. Please try again.";
  return "That did not work.";
}

async function toResult<T>(response: Response, endpoint: string): Promise<T> {
  // 204, and any empty body. `PUT /flags/:key`-style routes may answer 200 with
  // no content depending on the proxy; treat both as "no value".
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    if (response.ok) return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = undefined;
  if (text !== "") {
    try {
      payload = JSON.parse(text);
    } catch {
      // nginx or the platform answered with HTML (a 502 page, a captive portal).
      // Do not surface that markup — it is not a message for a human.
      payload = undefined;
    }
  }

  if (response.ok) return payload as T;

  const { message, details } = parseErrorEnvelope(payload);

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * A 5xx SENTENCE THE SERVER DELIBERATELY WROTE REACHES THE OWNER. A CRASH
   * STILL DOES NOT.
   *
   * THIS USED TO DISCARD EVERY 5xx MESSAGE, and that was two layers sanitising
   * the same thing with the second undoing the first. `describeError` in
   * apps/api/src/app.ts already flattens anything that is not a deliberate
   * `HttpError` — a thrown `Error`, a Prisma failure, a stack — to the bare
   * string {@link SERVER_CRASH_MESSAGE}. So by the time a 5xx carries ANY OTHER
   * sentence, a developer wrote that sentence for a human to read.
   *
   * WHAT THAT COST, CONCRETELY. `POST /quotations/:id/approval-link` answers
   * 500 with "PUBLIC_APP_BASE_URL is not set on this server … Add it to the
   * repo-root .env" — the variable named, the file named, the fix named. The
   * owner read "Something went wrong. Please try again." and tried again. Every
   * deliberate 5xx in this API has that shape: a configuration or invariant
   * sentence in plain English (twelve `HttpError(500, …)` sites at the time of
   * writing), none of them carrying SQL, a connection string or a stack —
   * because the only way to GET a stack into the envelope is to throw something
   * that is not an `HttpError`, and that is exactly what gets flattened.
   *
   * THE FALLBACK SURVIVES FOR THE TWO CASES IT WAS ACTUALLY FOR: a crash
   * (flattened to `SERVER_CRASH_MESSAGE`, which is a status line, not a message
   * for anybody), and an empty or absent message — nginx's 502 HTML, already
   * reduced to `undefined` above, or a proxy answering with nothing.
   *
   * THE 4xx PATH IS UNTOUCHED. It passed the server's words through before and
   * still does; the condition below only ever ADDS a case in which a message is
   * kept.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const safeMessage =
    message === null || message === "" || (response.status >= 500 && message === SERVER_CRASH_MESSAGE)
      ? fallbackMessage(response.status)
      : message;

  throw new ApiError(response.status, safeMessage, { details, endpoint });
}

/* -------------------------------------------------------------------------- */
/* The silent refresh (§11.1 — "keep him permanently signed in")               */
/* -------------------------------------------------------------------------- */

/**
 * In-flight refresh, shared by every request that 401s at the same moment.
 *
 * The home screen fires several queries at once (§11.2); without this, a token
 * that expired while the phone was in a pocket would produce four simultaneous
 * `/auth/refresh` calls. Three of them would present a refresh token that the
 * first call has already rotated away, and the owner would be bounced to the
 * login screen despite having a perfectly good session. One promise, awaited by
 * all four.
 */
let refreshInFlight: Promise<AuthTokens | null> | null = null;

async function performRefresh(): Promise<AuthTokens | null> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null) return null;

  let response: Response;
  try {
    response = await fetch(buildUrl("/auth/refresh", undefined), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      // §7 (packages/types/auth.ts): the refresh token travels in the BODY, not
      // in the Authorization header — the header is reserved for access tokens
      // and the API refuses a refresh token presented there.
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    /*
     * The refresh itself failed to reach the server. This is NOT an expired
     * session — it is a train tunnel. Returning null here would sign the owner
     * out for being offline, which §11.1 rules out ("keep him permanently
     * signed in"). Signalling it as a network failure lets the caller surface
     * "no internet" and keep the session intact.
     */
    throw new ApiError(NETWORK_FAILURE_STATUS, fallbackMessage(NETWORK_FAILURE_STATUS), {
      endpoint: "POST /auth/refresh",
    });
  }

  if (!response.ok) return null;

  const payload: unknown = await response.json().catch(() => undefined);
  // Validated with the SHARED schema (§13) rather than cast: a malformed
  // response must not be written into storage as if it were a session.
  const parsed = AuthTokensSchema.safeParse(payload);
  if (!parsed.success) return null;

  saveSession(parsed.data);
  return parsed.data;
}

function refreshOnce(): Promise<AuthTokens | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/* -------------------------------------------------------------------------- */
/* The request                                                                 */
/* -------------------------------------------------------------------------- */

async function send(
  method: HttpMethod,
  path: string,
  options: ApiRequestOptions,
  accessToken: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken !== null) headers["Authorization"] = `Bearer ${accessToken}`;

  const init: RequestInit = { method, headers };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal !== undefined) init.signal = options.signal;

  return fetch(buildUrl(path, options.query), init);
}

/**
 * The bearer + refresh + retry dance, WITHOUT interpreting the body.
 *
 * Split out of {@link request} so that the two things the app fetches — JSON and
 * PDF bytes (§7 `GET /invoices/:id/pdf`) — share exactly one implementation of
 * the auth rules. Duplicating the 401 handling for the PDF path is how the
 * download button ends up being the one place a stale token signs the owner out
 * (§11.1: "keep him permanently signed in").
 */
async function sendAuthenticated(
  method: HttpMethod,
  path: string,
  options: ApiRequestOptions,
  endpoint: string,
): Promise<Response> {
  const anonymous = options.anonymous === true;

  let response: Response;
  try {
    response = await send(method, path, options, anonymous ? null : getAccessToken());
  } catch (cause) {
    // An aborted request is not a failure to report — it is a component that
    // unmounted or a newer search superseding this one.
    if (options.signal?.aborted === true) throw cause;
    throw new ApiError(NETWORK_FAILURE_STATUS, fallbackMessage(NETWORK_FAILURE_STATUS), {
      endpoint,
      cause,
    });
  }

  /*
   * ONE refresh, ONE retry. Not a loop: if the reissued token is also refused,
   * the problem is the session, not the token, and retrying forever would spin
   * silently while the owner stares at a spinner.
   */
  if (response.status === 401 && !anonymous && options.reauthenticating !== true) {
    const tokens = await refreshOnce(); // may throw ApiError(0) when offline
    if (tokens === null) {
      expireSession();
      throw new ApiError(401, fallbackMessage(401), { endpoint });
    }

    try {
      response = await send(method, path, options, tokens.accessToken);
    } catch (cause) {
      if (options.signal?.aborted === true) throw cause;
      throw new ApiError(NETWORK_FAILURE_STATUS, fallbackMessage(NETWORK_FAILURE_STATUS), {
        endpoint,
        cause,
      });
    }

    if (response.status === 401) {
      expireSession();
      throw new ApiError(401, fallbackMessage(401), { endpoint });
    }
  }

  return response;
}

export async function request<T>(
  method: HttpMethod,
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const endpoint = `${method} ${path}`;
  return toResult<T>(await sendAuthenticated(method, path, options, endpoint), endpoint);
}

/* -------------------------------------------------------------------------- */
/* Binary responses — the PDF routes (§7, §9)                                  */
/* -------------------------------------------------------------------------- */

/** A fetched document: the bytes, and the name the server wants it saved under. */
export interface BinaryResponse {
  blob: Blob;
  /** From `Content-Disposition`, or the caller's fallback. Never a guess at the path. */
  filename: string;
  contentType: string;
}

/**
 * `filename="26-27-67 Lupin Limited.pdf"` out of a `Content-Disposition` header.
 *
 * The server already composed this (`invoicePdfFilename` in
 * apps/api/src/services/pdf) and it contains the document NUMBER — which is the
 * only thing that makes a folder of downloaded PDFs navigable. Rebuilding it on
 * the client would produce a second, subtly different naming scheme.
 *
 * `filename*=UTF-8''…` (RFC 5987) is preferred when present: a customer name
 * with a Devanagari or accented character travels percent-encoded there and as
 * a mangled ASCII approximation in the plain `filename`.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (header === null) return null;

  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1] !== undefined) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      /* a malformed encoding is not worth failing a download over */
    }
  }

  const plain = /filename\s*=\s*"([^"]+)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  const value = plain?.[1]?.trim();
  return value === undefined || value === "" ? null : value;
}

/**
 * Fetch a document rather than JSON — `GET /invoices/:id/pdf` and its quotation
 * twin (§7).
 *
 * WHY THIS CANNOT GO THROUGH `api.get`. `toResult` reads the body as text and
 * parses it as JSON; a PDF would come back as `undefined` with no error at all.
 *
 * WHY IT IS NOT AN `<a href>` OR AN `<iframe src>` POINTING AT THE ROUTE. The
 * PDF endpoints are authenticated with a BEARER token (§1.1 — bearer, not
 * cookies, so the Capacitor shell works unchanged), and a browser navigation
 * carries no Authorization header. The only way to open one is to fetch it with
 * the header and hand the resulting bytes to the viewer as an object URL.
 *
 * An error response here is still JSON, so it is routed through `toResult` to
 * become the same typed {@link ApiError} every other call produces.
 */
export async function requestBlob(
  path: string,
  options: Omit<ApiRequestOptions, "body"> & {
    fallbackFilename: string;
    /**
     * GET unless a body is given. The preview-PDF route is a POST because an
     * unsaved bill's lines do not fit in a query string — it still mutates
     * nothing, and the response is a document either way.
     */
    method?: "GET" | "POST";
    body?: unknown;
  },
): Promise<BinaryResponse> {
  const method = options.method ?? (options.body === undefined ? "GET" : "POST");
  const endpoint = `${method} ${path}`;
  const response = await sendAuthenticated(
    method,
    path,
    { ...options, headers: { Accept: "application/pdf", ...options.headers } },
    endpoint,
  );

  if (!response.ok) {
    // Throws — the API's error envelope is JSON even on a PDF route.
    await toResult<never>(response, endpoint);
  }

  const blob = await response.blob();
  return {
    blob,
    filename:
      filenameFromContentDisposition(response.headers.get("content-disposition")) ??
      options.fallbackFilename,
    contentType: response.headers.get("content-type") ?? "application/pdf",
  };
}

/**
 * The client. Use these rather than `request` directly — the method is then
 * part of the call site and shows up in a grep for "what writes to invoices".
 */
export const api = {
  get: <T>(path: string, options?: Omit<ApiRequestOptions, "body">): Promise<T> =>
    request<T>("GET", path, options ?? {}),

  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> =>
    request<T>("POST", path, { ...options, body }),

  put: <T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> =>
    request<T>("PUT", path, { ...options, body }),

  patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> =>
    request<T>("PATCH", path, { ...options, body }),

  delete: <T>(path: string, options?: ApiRequestOptions): Promise<T> =>
    request<T>("DELETE", path, options ?? {}),
} as const;

/* -------------------------------------------------------------------------- */
/* §17.1 — the ONE unauthenticated client, for the customer approval portal     */
/* -------------------------------------------------------------------------- */

/**
 * ═════════════════════════════════════════════════════════════════════════════
 * IT MUST NOT GO THROUGH `sendAuthenticated`, AND THAT IS THE WHOLE REASON THIS
 * EXISTS RATHER THAN A FLAG ON `request`.
 *
 * That path attaches a bearer token, and on a 401 it REFRESHES the session and
 * then `expireSession()`s — which signs the OWNER out. The portal is opened by a
 * customer on their own phone, but it is also opened by the owner checking the
 * link works, in the same browser he is signed into. A 410 on a stale link must
 * never be able to reach the session machinery at all, and the only way to
 * guarantee that is a client that has no session to reach.
 *
 * It also sends NO `Authorization` header, deliberately: the API's public routes
 * do not read one, and putting the owner's access token on a request whose
 * response is a customer-facing page is exactly the kind of accident that turns
 * a public endpoint into an authenticated one nobody realises is authenticated.
 *
 * THE TOKEN GOES IN THE BODY, NEVER THE PATH. `endpoints.publicApproval*` are
 * static strings for that reason — see the API's routes/public-approval.ts,
 * where the argument (nginx access logs, `Referer`) is written out in full.
 * ═════════════════════════════════════════════════════════════════════════════
 */
async function sendPublic(path: string, body: unknown): Promise<Response> {
  const endpoint = `POST ${path}`;
  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      // No credentials, no cookies, nothing ambient. The link is the credential.
      credentials: "omit",
    });
  } catch (cause) {
    throw new ApiError(NETWORK_FAILURE_STATUS, fallbackMessage(NETWORK_FAILURE_STATUS), {
      endpoint,
      cause,
    });
  }
}

/** JSON from a public route. Errors become the same typed {@link ApiError}. */
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  return toResult<T>(await sendPublic(path, body), `POST ${path}`);
}

/** A PDF from a public route — the portal's Download button. */
export async function publicBlob(
  path: string,
  body: unknown,
  fallbackFilename: string,
): Promise<BinaryResponse> {
  const endpoint = `POST ${path}`;
  const response = await sendPublic(path, body);

  if (!response.ok) {
    // Throws — the API's error envelope is JSON even on a PDF route.
    await toResult<never>(response, endpoint);
  }

  const blob = await response.blob();
  return {
    blob,
    filename:
      filenameFromContentDisposition(response.headers.get("content-disposition")) ?? fallbackFilename,
    contentType: response.headers.get("content-type") ?? "application/pdf",
  };
}

export { ApiError, isApiError } from "@/lib/api-error";
export type { ApiErrorDetail } from "@/lib/api-error";
